import { c as _c } from "react/compiler-runtime";
import { IcoCamera40 } from '@onefootprint/icons';
import { DocumentUploadSettings, type IdDocImageTypes } from '@onefootprint/types';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { HeaderTitle, NavigationHeader } from '@/idv/components';
import IdDocPhotoButtons from '../../../components/id-doc-photo-buttons';
import { isFront, isSelfie } from '../../../utils/capture';
import { useIdDocMachine } from '../../components/machine-provider';
import docTypeTranslationKeyMap from '../../utils/doc-type-translation-key-map';
type MobileFallbackUploadProps = {
  imageType: `${IdDocImageTypes}`;
};
const MobileFallbackUpload = t0 => {
  const $ = _c(35);
  const {
    imageType
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "document-flow.id-doc.pages"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t1);
  const [state, send] = useIdDocMachine();
  const {
    hasBadConnectivity,
    idDoc,
    allowPdf,
    requirement
  } = state.context;
  const allowUpload = requirement.uploadSettings !== DocumentUploadSettings.captureOnlyOnMobile && requirement.uploadSettings !== DocumentUploadSettings.requireMobileCapture;
  let t2;
  if ($[1] !== imageType || $[2] !== t) {
    t2 = () => {
      if (isSelfie(imageType)) {
        return t(_temp);
      }
      if (isFront(imageType)) {
        return t(_temp2);
      }
      return t(_temp3);
    };
    $[1] = imageType;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getTitle = t2;
  let t3;
  if ($[4] !== idDoc || $[5] !== imageType || $[6] !== t) {
    t3 = () => {
      if (isSelfie(imageType)) {
        return t(_temp4);
      }
      if (!idDoc.type) {
        return null;
      }
      const lastKey = docTypeTranslationKeyMap[idDoc.type];
      if (isFront(imageType)) {
        return t($_3 => $_3["mobile-front-photo-fallback-upload"].subtitle[lastKey]);
      }
      return t($_4 => $_4["mobile-back-photo-fallback-upload"].subtitle[lastKey]);
    };
    $[4] = idDoc;
    $[5] = imageType;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const getSubtitle = t3;
  let t4;
  if ($[8] !== getTitle) {
    t4 = getTitle();
    $[8] = getTitle;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const title = t4;
  let t5;
  if ($[10] !== getSubtitle) {
    t5 = getSubtitle();
    $[10] = getSubtitle;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const subtitle = t5;
  if (!subtitle) {
    return null;
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 1,
      transition: {
        duration: 0.3
      }
    };
    t8 = {
      opacity: 0
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[15] !== send) {
    t9 = <NavigationHeader leftButton={{
      variant: "back",
      onBack: () => send({
        type: "navigatedToPrev"
      })
    }} position="floating" />;
    $[15] = send;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== subtitle || $[18] !== title) {
    t10 = <HeaderTitle title={title} subtitle={subtitle} icon={IcoCamera40} display="flex" flexDirection="column" />;
    $[17] = subtitle;
    $[18] = title;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== send) {
    t11 = payload => send({
      type: "receivedImage",
      payload
    });
    $[20] = send;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== allowUpload || $[23] !== imageType) {
    t12 = isSelfie(imageType) || !allowUpload;
    $[22] = allowUpload;
    $[23] = imageType;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = isSelfie(imageType) ? "user" : "environment";
  let t14;
  if ($[25] !== allowPdf || $[26] !== hasBadConnectivity || $[27] !== t11 || $[28] !== t12 || $[29] !== t13) {
    t14 = <IdDocPhotoButtons onComplete={t11} allowPdf={allowPdf} hideUploadButton={t12} nativeCameraFacingMode={t13} isNativeCameraCapture={true} uploadFirst={true} hasBadConnectivity={hasBadConnectivity} fallbackUpload={true} />;
    $[25] = allowPdf;
    $[26] = hasBadConnectivity;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t10 || $[32] !== t14 || $[33] !== t9) {
    t15 = <FlexColumnMotionContainer initial={t6} animate={t7} exit={t8}>{t9}{t10}{t14}</FlexColumnMotionContainer>;
    $[31] = t10;
    $[32] = t14;
    $[33] = t9;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
};
export const FlexColumnMotionContainer = t0 => {
  const $ = _c(6);
  let children;
  let motionProps;
  if ($[0] !== t0) {
    ({
      children,
      ...motionProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = motionProps;
  } else {
    children = $[1];
    motionProps = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== motionProps) {
    t1 = <motion.div className="flex flex-col justify-center items-center flex-1 h-full gap-4 p-4" {...motionProps}>{children}</motion.div>;
    $[3] = children;
    $[4] = motionProps;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export default MobileFallbackUpload;
function _temp($) {
  return $["mobile-selfie-fallback-upload"].title;
}
function _temp2($_0) {
  return $_0["mobile-front-photo-fallback-upload"].title;
}
function _temp3($_1) {
  return $_1["mobile-back-photo-fallback-upload"].title;
}
function _temp4($_2) {
  return $_2["mobile-selfie-fallback-upload"].subtitle;
}